import { CHECKBOX_STATUS } from 'ui/checkbox';
import { MergedPermission } from '../types';

const createUniquePermissions = (permissions: number[]) => {
  const unique = new Set(permissions);
  return Array.from(unique);
};

/**
 * Event handler for standard checkboxes
 */
export const manageSingleCheckbox = (prev: number[], id: number, requires: number[]): number[] =>
  createUniquePermissions(
    prev.includes(id) ? prev.filter((val) => val !== id) : [...prev, ...requires, id],
  );

/**
 * Event handler to select all grouped children and their required permissions
 */
const addAllChildrenAndRequired = (
  prev: number[],
  children: number[],
  getPermissionById: (id: number) => MergedPermission | undefined,
): number[] => {
  const requiredBy = children.flatMap((child) => {
    const curr = getPermissionById(child);
    return curr?.requires || [];
  });

  return createUniquePermissions([...prev, ...children, ...requiredBy]);
};

/**
 * Event handler to unselect all grouped children
 */
const removeAllChildren = (prev: number[], children: number[]): number[] =>
  prev.filter((per) => !children.includes(per));

/**
 * Event handler for group checkbox (not associated with a permission number directly)
 */
export const manageGroupCheckbox = (
  prev: number[],
  children: number[],
  getPermissionById: (id: number) => MergedPermission | undefined,
): number[] => {
  const allSelected = children.every((item) => prev.includes(item));

  return allSelected
    ? removeAllChildren(prev, children)
    : addAllChildrenAndRequired(prev, children, getPermissionById);
};

/**
 * Creates checkbox status based on the current state
 */
export const getCheckboxStatus = (
  permission: MergedPermission,
  selectedPermissions: number[] = [],
): string => {
  const { id, children } = permission;
  if (children?.length) {
    const isAllChecked = children?.every((item) => selectedPermissions.includes(item));
    if (isAllChecked) return CHECKBOX_STATUS.CHECKED;
    const isNoneChecked = children?.every((item) => !selectedPermissions.includes(item));
    if (isNoneChecked) return CHECKBOX_STATUS.UNCHECKED;
    return CHECKBOX_STATUS.INDETERMINATE;
  }

  return selectedPermissions?.includes(id) ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED;
};
